import {useState, useEffect, useRef, useCallback} from 'react';
import {computeMinWidth} from "../utils/annotationUtils";
import { snapMovementDelta } from '../utils/annotationGroupOperations';

/**
 * Custom hook to handle annotation resizing
 */
const useAnnotationResize = ({
  annotation,
  timeSignatures,
  startX,
  pixelsPerBeat,
  otherAnnotations,
  calculatePosition,
  onResize,
  scrollPosition,
  // minWidth = pixelsPerBeat / 2,
  onResizePreview,
  onResizeCommit,
  totalWidth,
  onSelect,
  beatsPerMeasureMap,
  currentGridLevel,
  movementStep,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const finalWidthRef = useRef(null);
  const absStartX = startX + scrollPosition;

  const getMinXToRight = useCallback((startX, rightmostPosition) => {
    let res = rightmostPosition;
    for (let other of otherAnnotations) {
      const otherStart = calculatePosition(other.startMeasure, other.startBeat, other.startSubdivision || 0);
      if (startX <= otherStart) { // cannot extend to the annotations to the right
        res = Math.min(res, otherStart);
      }
    }
    return res;
  }, [otherAnnotations, calculatePosition]);

  const getMaxXToLeft = useCallback((leftmostPosition, endX) => {
    let res = leftmostPosition;
    for (let other of otherAnnotations) {
      const otherEnd = calculatePosition(other.endMeasure, other.endBeat, other.endSubdivision || 0);
      if (otherEnd <= endX) {
        res = Math.max(res, otherEnd);
      }
    }
    return res;
  }, [otherAnnotations, calculatePosition]);

  useEffect(() => {
    if (!isResizing) return;

    // Handler for mouse movement during resize
    const handleMouseMove = (e) => {
      // Get mouse position
      const stage = window.Konva.stages[0];
      if (!stage) return;

      const containerRect = stage.container().getBoundingClientRect();

      // Get accurate scroll position directly from the scroll container element
      // This ensures exact synchronization with the time grid
      const scrollContainer = document.querySelector('.w-\\[90\\%\\].overflow-auto');
      const currentScrollPosition = scrollContainer ? scrollContainer.scrollLeft : scrollPosition;

      let pointer = {
        x: e.clientX - containerRect.left + currentScrollPosition,
        y: e.clientY - containerRect.top
      };

      // Fix: Use the actual 121.1.1 position instead of totalWidth minus pixelsPerBeat
      const rightmostPosition = calculatePosition(beatsPerMeasureMap.length + 1, 1, 1);
      pointer.x = Math.max(0, Math.min(pointer.x, rightmostPosition));

      if (resizeDirection === 'right') {
        const originalEndX = calculatePosition(
          annotation.endMeasure,
          annotation.endBeat,
          annotation.endSubdivision || 0
        );
        const originalWidth = originalEndX - absStartX;
        const neighborBoundary = getMinXToRight(absStartX, rightmostPosition);
        const availableWidth = neighborBoundary - absStartX;
        const minWidth = computeMinWidth(annotation.startMeasure, annotation.startBeat, annotation.startSubdivision, beatsPerMeasureMap, pixelsPerBeat, timeSignatures, currentGridLevel);

        if (availableWidth < minWidth) {
          onResizePreview(null);
          finalWidthRef.current = null;
          return;
        }

        pointer.x = Math.min(pointer.x, neighborBoundary);
        const delta = snapMovementDelta(pointer.x - originalEndX, movementStep);
        const newWidth = Math.max(originalWidth + delta, minWidth);

        // Update width + direction
        onResizePreview({ width: newWidth, direction: resizeDirection });
        finalWidthRef.current = { width: newWidth, direction: resizeDirection };
      } else {
        const endX = calculatePosition(
          annotation.endMeasure,
          annotation.endBeat,
          annotation.endSubdivision || 0
        );
        const minWidth = computeMinWidth(annotation.endMeasure, annotation.endBeat, annotation.endSubdivision, beatsPerMeasureMap, pixelsPerBeat, timeSignatures, currentGridLevel);
        const neighborBoundary = getMaxXToLeft(0, endX);
        const availableWidth = endX - neighborBoundary;

        if (availableWidth < minWidth) {
          onResizePreview(null);
          finalWidthRef.current = null;
          return;
        }

        pointer.x = Math.max(pointer.x, neighborBoundary);
        const rawStartX = Math.min(pointer.x, endX - minWidth);
        const delta = snapMovementDelta(rawStartX - absStartX, movementStep);
        const newWidth = endX - (absStartX + delta);

        // Update width + direction
        onResizePreview({ width: newWidth, direction: resizeDirection });
        finalWidthRef.current = { width: newWidth, direction: resizeDirection };
      }
    };

    // Handler for mouse up to end resizing
    const handleMouseUp = () => {
      if (finalWidthRef.current) {
        onResizeCommit(finalWidthRef.current);
      } else {
        // No resize actually occurred
        console.warn("Resize was initiated but not moved; skipping commit.");
      }
      finalWidthRef.current = null;
      setIsResizing(false);
      document.body.style.cursor = 'default';
      
      // Remove event listeners
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    // Add DOM event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Cleanup function
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    absStartX, resizeDirection, isResizing, startX, pixelsPerBeat, onResize,
    otherAnnotations, calculatePosition, annotation, scrollPosition,
    getMinXToRight, getMaxXToLeft, onResizeCommit, onResizePreview, totalWidth,
    beatsPerMeasureMap, timeSignatures, currentGridLevel, movementStep
  ]);

  // Start resizing (either from left or right)
  const startResizing = (direction) => {
    setIsResizing(true);
    setResizeDirection(direction);
    onSelect();
  };

  return { isResizing, startResizing };
};

export default useAnnotationResize;
